"use client";

import { useEffect, useRef } from "react";
import { useTranslations } from "next-intl";

const LABELS = [
  "selection",
  "listing",
  "keywords",
  "ads",
  "competitors",
  "inventory",
  "afterSales",
  "profit",
  "sop",
  "rpa",
] as const;

const N = LABELS.length;

const FOCAL_START_X = 36;
const FOCAL_END_X = 56;
const FOCAL_Y = 150;
const HUB_X = 286;
const HUB_Y = 150;

const MESSY_END: [number, number][] = [
  [682, 44], [532, 102], [802, 72], [598, 36], [868, 128],
  [518, 192], [736, 218], [648, 158], [826, 252], [562, 272],
];
const MESSY_C1: [number, number][] = [
  [210, 18], [165, 228], [318, 12], [155, 168], [295, 198],
  [95, 85], [255, 262], [188, 38], [348, 188], [172, 278],
];
const MESSY_C2: [number, number][] = [
  [418, 58], [368, 118], [548, 88], [308, 72], [628, 42],
  [288, 168], [508, 208], [388, 138], [668, 218], [328, 248],
];

const ORDERED_END: [number, number][] = Array.from({ length: N }, (_, i) => [
  846,
  30 + (i * (270 - 30)) / (N - 1),
]);
const ORD_C1 = ORDERED_END.map(([x, y]): [number, number] => [
  HUB_X + (x - HUB_X) * 0.32,
  HUB_Y + (y - HUB_Y) * 0.06,
]);
const ORD_C2 = ORDERED_END.map(([x, y]): [number, number] => [
  HUB_X + (x - HUB_X) * 0.68,
  HUB_Y + (y - HUB_Y) * 0.38,
]);

const TOP_TRIGGER = 460;
const SCROLL_TRAVEL = 400;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function easeInOutCubic(t: number): number {
  if (t <= 0) {
    return 0;
  }

  if (t >= 1) {
    return 1;
  }

  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

export function HomeOpsNet() {
  const t = useTranslations("site.homePage.opsNet");
  const anchorRef = useRef<HTMLElement>(null);
  const focalRef = useRef<SVGCircleElement>(null);
  const pathRefs = useRef<(SVGPathElement | null)[]>([]);
  const nodeRefs = useRef<(SVGCircleElement | null)[]>([]);
  const textRefs = useRef<(SVGTextElement | null)[]>([]);

  useEffect(() => {
    const anchor = anchorRef.current;
    const focal = focalRef.current;

    if (!anchor || !focal) {
      return;
    }

    const setProgress = (raw: number) => {
      const t = easeInOutCubic(Math.max(0, Math.min(1, raw)));
      const fx = lerp(FOCAL_START_X, FOCAL_END_X, t);

      focal.setAttribute("cx", String(fx));
      focal.setAttribute("cy", String(FOCAL_Y));

      for (let i = 0; i < N; i += 1) {
        const ex = lerp(MESSY_END[i][0], ORDERED_END[i][0], t);
        const ey = lerp(MESSY_END[i][1], ORDERED_END[i][1], t);
        let c1x = lerp(MESSY_C1[i][0], ORD_C1[i][0], t);
        let c1y = lerp(MESSY_C1[i][1], ORD_C1[i][1], t);
        let c2x = lerp(MESSY_C2[i][0], ORD_C2[i][0], t);
        let c2y = lerp(MESSY_C2[i][1], ORD_C2[i][1], t);

        if (t > 0.12) {
          const u = Math.min(1, (t - 0.12) / 0.88);
          const hx = lerp(fx, HUB_X, u * 0.92);
          const hy = lerp(FOCAL_Y, HUB_Y, u * 0.92);

          c1x = lerp(c1x, hx + (ex - hx) * 0.28, u);
          c1y = lerp(c1y, hy + (ey - hy) * 0.08, u);
          c2x = lerp(c2x, hx + (ex - hx) * 0.62, u);
          c2y = lerp(c2y, hy + (ey - hy) * 0.32, u);
        }

        pathRefs.current[i]?.setAttribute(
          "d",
          `M ${fx} ${FOCAL_Y} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${ex} ${ey}`,
        );
        nodeRefs.current[i]?.setAttribute("cx", String(ex));
        nodeRefs.current[i]?.setAttribute("cy", String(ey));
        textRefs.current[i]?.setAttribute("x", String(ex + lerp(6, 9, t)));
        textRefs.current[i]?.setAttribute("y", String(ey));
      }
    };

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setProgress(1);
      return;
    }

    let rafId: number | null = null;

    const tick = () => {
      rafId = null;
      setProgress(
        (TOP_TRIGGER - anchor.getBoundingClientRect().top) / SCROLL_TRAVEL,
      );
    };

    const schedule = () => {
      if (rafId === null) {
        rafId = requestAnimationFrame(tick);
      }
    };

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    schedule();

    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return (
    <article
      className="shouye-guanli__panel shouye-guanli__panel--net"
      id="shouye-guanli-panel-net"
      ref={anchorRef}
    >
      <div className="shouye-guanli__net-layout">
        <div className="shouye-guanli__net-head">
          <h3 className="shouye-guanli__wide-title">{t("title")}</h3>
          <p className="shouye-guanli__net-sub">{t("sub")}</p>
        </div>
        <div className="shouye-guanli__net-wrap">
          <svg
            className="shouye-guanli__net-svg"
            viewBox="0 0 1000 300"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
            overflow="visible"
          >
            <defs>
              <linearGradient id="sgl-net-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#6366f1" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#6366f1" stopOpacity="0.9" />
              </linearGradient>
            </defs>
            <g className="shouye-guanli__net-paths">
              {LABELS.map((label, i) => (
                <path
                  key={label}
                  ref={(node) => {
                    pathRefs.current[i] = node;
                  }}
                  className={`shouye-guanli__net-path${
                    i === 0 ? " shouye-guanli__net-path--hero" : ""
                  }`}
                  stroke={i === 0 ? "url(#sgl-net-grad)" : "#6366f1"}
                  strokeOpacity={0.38 + (i % 6) * 0.07}
                />
              ))}
            </g>
            <g className="shouye-guanli__net-nodes">
              {LABELS.map((label, i) => (
                <circle
                  key={label}
                  ref={(node) => {
                    nodeRefs.current[i] = node;
                  }}
                  className="shouye-guanli__net-node"
                  r="3.25"
                />
              ))}
            </g>
            <g className="shouye-guanli__net-labels-svg">
              {LABELS.map((label, i) => (
                <text
                  key={label}
                  ref={(node) => {
                    textRefs.current[i] = node;
                  }}
                  className="shouye-guanli__net-txt"
                  dominantBaseline="middle"
                >
                  {t(`labels.${label}`)}
                </text>
              ))}
            </g>
            <circle className="shouye-guanli__net-focal" r="7" ref={focalRef} />
          </svg>
        </div>
      </div>
    </article>
  );
}
